import { CollapsibleCard } from '@teamsnap/snap-ui';
import { CollapsibleCardSkeleton } from 'components/common';
import { BalanceChangeSummary } from 'components/common/balance-adjustment/BalanceChangeSummary';
import { CollectBalance } from 'components/common/balance-adjustment/CollectBalance';
import { OverPaidText } from 'components/common/balance-adjustment/OverPaidText';
import { getAmpliBalanceChangeType, useAmplitude } from 'hooks/amplitude';
import {
  LoadingType,
  usePostRegEditActions,
  usePostRegEditDrawer,
} from 'hooks/postRegEditDrawer';
import * as React from 'react';
import { useEffect } from 'react';
import { InstallmentEditable } from '../../types';

export const BalanceChange = () => {
  const { loading, previewLineItems, pendingPayment } = usePostRegEditDrawer();
  const { getPreviewLineItems, updatePendingPayment, validatePendingPayment } =
    usePostRegEditActions();
  const { amplitudeTrack } = useAmplitude();
  const showMultipleRegistrationWarning =
    previewLineItems?.registrationsInOrder > 1;

  useEffect(() => {
    getPreviewLineItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      typeof previewLineItems?.newBalance === 'undefined' ||
      typeof previewLineItems?.currentBalance === 'undefined'
    ) {
      return;
    }
    const balanceChangeType = getAmpliBalanceChangeType(
      previewLineItems?.newBalance,
      previewLineItems?.currentBalance
    );
    balanceChangeType && amplitudeTrack.preFormReviewed(balanceChangeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLineItems?.newBalance, previewLineItems?.currentBalance]);

  useEffect(() => {
    validatePendingPayment();
  }, [pendingPayment?.installments]);

  if (
    !previewLineItems ||
    (
      previewLineItems.currentBalance === 0 &&
      previewLineItems.newBalance === 0 &&
      previewLineItems.canceledLineItems?.length === 0 &&
      previewLineItems.newLineItems?.length === 0
    )
  ) {
    return null;
  }

  return loading[LoadingType.GetPreviewLineItems] ? (
    <CollapsibleCardSkeleton />
  ) : (
    <div className="sui-flex sui-flex-col sui-gap-4">
      <CollapsibleCard.Root isDefaultOpen>
        <CollapsibleCard.Header title="Balance Change" />
        <CollapsibleCard.Content>
          {showMultipleRegistrationWarning && (
            <p
              className="sui-body sui-text-gray-60 sui-mb-2"
              data-test-id="multiple-registration-warning"
            >
              The balance reflects the total for the entire order, not just the
              participant being edited.
            </p>
          )}
          <BalanceChangeSummary {...previewLineItems} />
          {previewLineItems?.newBalance > 0 && (
            <CollectBalance
              newBalance={previewLineItems.newBalance}
              updateInstallments={(
                installments: InstallmentEditable[],
                editType
              ) => {
                updatePendingPayment({ installments });
                editType && amplitudeTrack.preInstallmentsEdited(editType);
              }}
              {...pendingPayment}
            />
          )}
          {previewLineItems?.newBalance < 0 && <OverPaidText />}
        </CollapsibleCard.Content>
      </CollapsibleCard.Root>
    </div>
  );
};
